/**
 * ログイン済み来場者向けの屋台評価ptコード交換パネル
 */

import React, { useEffect, useState } from 'react';
import AppButton from '../../../shared/components/AppButton';
import { Text, TextInput, View } from '../../../shared/components/WebPrimitives';
import { INPUT_PLACEHOLDER_COLOR } from '../../../shared/constants/accessibility';

/**
 * 屋台評価ptコード交換パネル
 * @param {Object} props - プロパティ
 * @param {boolean} props.isClaiming - 交換処理中状態
 * @param {boolean} [props.isDisabled] - 未ログイン・オフライン時の送信停止
 * @param {Object | null} props.claimResult - pt付与結果
 * @param {string} props.errorMessage - 交換エラー
 * @param {(code: string) => Promise<void>} props.onClaim - 交換処理
 * @returns {JSX.Element} ptコード交換UI
 */
const StallRatingRewardClaimPanel = ({
  isClaiming,
  claimResult,
  errorMessage,
  onClaim,
  isDisabled = false,
}) => {
  /** 入力中の12文字ptコード */
  const [rewardCode, setRewardCode] = useState('');

  useEffect(() => {
    if (claimResult) {
      setRewardCode('');
    }
  }, [claimResult]);

  /**
   * 入力を大文字英数字12文字へ正規化する
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handleChangeRewardCode = (value) => {
    setRewardCode(
      String(value ?? '')
        .toUpperCase()
        .replace(/[^23456789ABCDEFGHJKLMNPQRSTUVWXYZ]/g, '')
        .slice(0, 12),
    );
  };

  return (
    <View className="rounded-none border-2 border-app-quest-night bg-[#FFF8DD] p-4">
      <Text aria-level={2} className="text-base font-extrabold text-app-quest-night" role="heading">
        匿名回答のptを受け取る
      </Text>
      <Text className="mt-1 text-xs leading-5 text-app-text-secondary">
        匿名で屋台ランキングに回答したとき保存した12文字のpt受取コードを入力してください。屋台のQRをもう一度読み取る必要はありません。
      </Text>

      {claimResult ? (
        <View
          accessibilityLiveRegion="polite"
          className="mt-3 border-2 border-app-quest-gold bg-amber-50 p-3"
          testID="stall-reward-claim-success"
        >
          <Text className="text-sm font-extrabold text-app-quest-night">
            {claimResult.pointsGranted} pt を受け取りました
          </Text>
          <Text className="mt-1 text-xs text-app-text">
            {claimResult.stallName} / 所持pt: {claimResult.currentPoint}
          </Text>
        </View>
      ) : null}

      {errorMessage ? (
        <View className="mt-3 border-2 border-app-danger bg-red-50 p-3" role="alert">
          <Text className="text-sm font-semibold text-app-danger">{errorMessage}</Text>
        </View>
      ) : null}

      <Text className="mb-1 mt-3 text-sm font-semibold text-app-text">pt受取コード</Text>
      <TextInput
        accessibilityLabel="匿名回答の12文字pt受取コード"
        aria-invalid={Boolean(errorMessage)}
        autoCapitalize="characters"
        autoCorrect={false}
        className="rounded-none border-2 border-app-quest-night bg-white px-4 py-3 font-mono text-base tracking-widest text-app-quest-night"
        maxLength={12}
        editable={!isClaiming}
        onChangeText={handleChangeRewardCode}
        placeholder="12文字のコード"
        placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
        testID="stall-reward-code-input"
        value={rewardCode}
      />
      <View className="mt-3">
        <AppButton
          disabled={isDisabled || isClaiming || rewardCode.length !== 12}
          label={isClaiming ? 'pt受取中...' : 'pt受取コードを交換'}
          onPress={() => onClaim(rewardCode)}
        />
      </View>
    </View>
  );
};

export default StallRatingRewardClaimPanel;
